import React, { useCallback, useRef, useState } from 'react';
import { View } from 'react-native';
import { Button, Card, DataTable, Headline, Text } from 'react-native-paper';
import { colors } from 'shared/styles';
import styled from 'utils/styled';
import {
  ControlPoint,
  DEVICE_INFORMATION_SERVICE_UUID,
  ErgType,
  FTMS_CONTROL_CHARACTERISTIC_UUID,
  FTMS_SERVICE_UUID,
  FIRMWARE_REVISION_CHARACTERISTIC_UUID,
  HARDWARE_REVISION_CHARACTERISTIC_UUID,
  MANUFACTURER_NAME_CHARACTERISTIC_UUID,
  parseFtmsData,
  rawBytesString,
  rawBytesToHex,
  ROW_FTMS_CHARACTERISTIC_UUID,
  BIKE_FTMS_CHARACTERISTIC_UUID,
  RUN_FTMS_CHARACTERISTIC_UUID,
  RowerData,
  BikeData,
  RunData,
} from './ftmsUtils';

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export interface DeviceInfo {
  manufacturer?: string;
  firmwareRevision?: string;
  hardwareRevision?: string;
}

export interface FTMSDataPoint {
  timestamp: string;
  rawBytes: string;
  rawHex: string[];
  data: RowerData | BikeData | RunData;
}

export interface ConnectionLogEntry {
  step: string;
  detail?: string;
  timestamp: string;
}

const ActionsContainer = styled.view({
  flex: 1,
  flexDirection: 'row',
  alignItems: 'center',
  margin: 10,
  flexWrap: 'wrap',
});

// Connection flow matches mobile connectFTMS.ts for troubleshooting
async function connect(onLog: (entry: ConnectionLogEntry) => void): Promise<{
  characteristic: any;
  deviceInfo: DeviceInfo;
  ergType: ErgType;
  controlPointUsed: boolean;
}> {
  const bluetooth = (navigator as any).bluetooth;
  if (!bluetooth) {
    return Promise.reject(new Error('Web Bluetooth not available'));
  }

  const log = (step: string, detail?: string) =>
    onLog({
      step,
      detail,
      timestamp: new Date().toISOString(),
    });

  log('1. Requesting device (filters: FTMS service)');
  const device = await bluetooth.requestDevice({
    filters: [{ services: [FTMS_SERVICE_UUID] }],
    optionalServices: [DEVICE_INFORMATION_SERVICE_UUID, FTMS_SERVICE_UUID],
  });

  log('2. Connecting to GATT server');
  const server = await device.gatt!.connect();

  log('3. Waiting 1s (first connection delay, matches mobile)');
  await sleep(1000);

  const deviceInfo: DeviceInfo = {};

  try {
    log('4. Reading Device Information Service');
    const deviceInfoService = await server.getPrimaryService(
      DEVICE_INFORMATION_SERVICE_UUID
    );

    try {
      const manufacturerChar = await deviceInfoService.getCharacteristic(
        MANUFACTURER_NAME_CHARACTERISTIC_UUID
      );
      const manufacturerValue = await manufacturerChar.readValue();
      deviceInfo.manufacturer = new TextDecoder().decode(manufacturerValue);
      log('4a. Manufacturer', deviceInfo.manufacturer);
    } catch (e) {
      log('4a. Manufacturer (not available)');
    }

    try {
      const firmwareChar = await deviceInfoService.getCharacteristic(
        FIRMWARE_REVISION_CHARACTERISTIC_UUID
      );
      const firmwareValue = await firmwareChar.readValue();
      deviceInfo.firmwareRevision = new TextDecoder().decode(firmwareValue);
      log('4b. Firmware', deviceInfo.firmwareRevision);
    } catch (e) {
      log('4b. Firmware (not available)');
    }

    try {
      const hardwareChar = await deviceInfoService.getCharacteristic(
        HARDWARE_REVISION_CHARACTERISTIC_UUID
      );
      const hardwareValue = await hardwareChar.readValue();
      deviceInfo.hardwareRevision = new TextDecoder().decode(hardwareValue);
      log('4c. Hardware', deviceInfo.hardwareRevision);
    } catch (e) {
      log('4c. Hardware (not available)');
    }
  } catch (e) {
    log('4. Device Information Service (not available)');
  }

  log('5. Getting FTMS service');
  const ftmsService = await server.getPrimaryService(FTMS_SERVICE_UUID);

  let controlPointUsed = false;
  try {
    log('6. Requesting control (RequestControl -> Reset -> StartSession)');
    const controlChar = await ftmsService.getCharacteristic(
      FTMS_CONTROL_CHARACTERISTIC_UUID
    );

    // Same sequence as mobile requestControl()
    const controls = [
      [ControlPoint.RequestControl],
      [ControlPoint.Reset],
      [ControlPoint.StartSession],
    ];

    for (let i = 0; i < controls.length; i++) {
      const control = new Uint8Array(controls[i]);
      await controlChar.writeValueWithResponse(control);
      log(`6${String.fromCharCode(97 + i)}. Wrote control ${controls[i][0]}`);
      await sleep(1000);
    }
    controlPointUsed = true;
  } catch (e) {
    log('6. Control point (not available, skipping)');
  }

  // Find data characteristic - try rower, bike, run (same order as mobile FTMS_ERG_TYPES)
  const dataCharUuids: { uuid: string; ergType: ErgType }[] = [
    { uuid: ROW_FTMS_CHARACTERISTIC_UUID, ergType: 'row' },
    { uuid: BIKE_FTMS_CHARACTERISTIC_UUID, ergType: 'bike' },
    { uuid: RUN_FTMS_CHARACTERISTIC_UUID, ergType: 'run' },
  ];

  let characteristic: any = null;
  let ergType: ErgType = 'row';

  for (const { uuid, ergType: type } of dataCharUuids) {
    try {
      characteristic = await ftmsService.getCharacteristic(uuid);
      ergType = type;
      log(`7. Found ${type} data characteristic`);
      break;
    } catch (e) {
      continue;
    }
  }

  if (!characteristic) {
    throw new Error('No FTMS data characteristic found (rower/bike/run)');
  }

  log('8. Starting notifications');
  await characteristic.startNotifications();

  return { characteristic, deviceInfo, ergType, controlPointUsed };
}

function downloadFile(content: string, filename: string) {
  const blob = new Blob([content], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const FTMSPage: React.FC = () => {
  const [latestData, setLatestData] = useState<
    RowerData | BikeData | RunData | null
  >(null);
  const [deviceName, setDeviceName] = useState<string | null>(null);
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(null);
  const [ergType, setErgType] = useState<ErgType>('row');
  const [connectionLog, setConnectionLog] = useState<ConnectionLogEntry[]>([]);
  const [dataLog, setDataLog] = useState<FTMSDataPoint[]>([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const characteristicRef = useRef<any>(null);

  const onConnect = useCallback(() => {
    setError(null);
    setConnectionLog([]);

    const onLog = (entry: ConnectionLogEntry) =>
      setConnectionLog((prev) => [...prev, entry]);

    connect(onLog)
      .then(({ characteristic, deviceInfo: info, ergType: type }) => {
        characteristicRef.current = characteristic;
        setConnected(true);
        setDeviceInfo(info);
        setErgType(type);
        setDeviceName(characteristic.service?.device?.name ?? null);

        characteristic.addEventListener(
          'characteristicvaluechanged',
          (event: Event) => {
            const ev = event as unknown as {
              target: { value: DataView };
            };
            const value = ev.target.value;
            const dataView = new DataView(
              value.buffer,
              value.byteOffset,
              value.byteLength
            );

            const parsed = parseFtmsData(dataView, type);
            const rawHex = rawBytesToHex(dataView);
            const rawBytes = rawBytesString(dataView);

            setLatestData((old) => ({ ...old, ...parsed }));
            setDataLog((prev) => [
              ...prev,
              {
                timestamp: new Date().toISOString(),
                rawHex,
                rawBytes,
                data: parsed,
              },
            ]);
          }
        );
      })
      .catch((err) => {
        setError(err?.message || String(err));
        console.error('FTMS connection error:', err);
      });
  }, []);

  const onDisconnect = useCallback(() => {
    if (characteristicRef.current) {
      characteristicRef.current.service?.device?.gatt?.disconnect();
      characteristicRef.current = null;
    }
    setConnected(false);
    setDeviceInfo(null);
    setErgType('row');
  }, []);

  const onExport = useCallback(() => {
    const exportData = {
      exportedAt: new Date().toISOString(),
      deviceName,
      deviceInfo,
      ergType,
      connectionLog,
      dataPoints: dataLog,
      summary: {
        totalNotifications: dataLog.length,
        latestData: latestData,
      },
    };
    const filename = `ftms-troubleshoot-${new Date().toISOString().slice(0, 10)}.json`;
    downloadFile(JSON.stringify(exportData, null, 2), filename);
  }, [dataLog, latestData, deviceName, deviceInfo, ergType, connectionLog]);

  const onClear = useCallback(() => {
    setDataLog([]);
    setLatestData(null);
  }, []);

  const hasBluetooth =
    typeof navigator !== 'undefined' && 'bluetooth' in navigator;

  return (
    <View>
      <Headline style={{ margin: 16 }}>FTMS Troubleshooting</Headline>
      <Card>
        <ActionsContainer>
          {!hasBluetooth && (
            <Text style={{ color: colors.destructiveColor, marginRight: 8 }}>
              Web Bluetooth is not available. Use Chrome/Edge over HTTPS.
            </Text>
          )}
          <Button
            mode="outlined"
            icon="bluetooth"
            onPress={onConnect}
            disabled={connected || !hasBluetooth}
            style={{ marginRight: 8, marginBottom: 8 }}
          >
            {connected ? 'Connected' : 'Connect'}
          </Button>
          <Button
            mode="outlined"
            icon="bluetooth-off"
            onPress={onDisconnect}
            disabled={!connected}
            style={{ marginRight: 8, marginBottom: 8 }}
          >
            Disconnect
          </Button>
          <Button
            mode="outlined"
            icon="download"
            onPress={onExport}
            disabled={dataLog.length === 0}
            style={{ marginRight: 8, marginBottom: 8 }}
          >
            Export to File ({dataLog.length} points)
          </Button>
          <Button
            mode="outlined"
            icon="delete"
            onPress={onClear}
            disabled={dataLog.length === 0}
            style={{ marginRight: 8, marginBottom: 8 }}
          >
            Clear Data
          </Button>
        </ActionsContainer>

        {error && (
          <Text
            style={{
              color: colors.destructiveColor,
              margin: 10,
              padding: 8,
              backgroundColor: colors.warningBgColor,
            }}
          >
            {error}
          </Text>
        )}

        <Text style={{ margin: 10, color: colors.textColor }}>
          Connect to an FTMS-compatible rowing machine to capture data. Uses the
          same connection flow as the mobile app for troubleshooting. Export the
          file and email it for troubleshooting.
        </Text>

        {connected && (deviceInfo || ergType) && (
          <View style={{ margin: 10 }}>
            <Text style={{ fontWeight: 'bold', marginBottom: 4 }}>
              Connection info (matches mobile flow)
            </Text>
            {deviceInfo?.manufacturer && (
              <Text style={{ color: colors.textColor }}>
                Manufacturer: {deviceInfo.manufacturer}
              </Text>
            )}
            {deviceInfo?.firmwareRevision && (
              <Text style={{ color: colors.textColor }}>
                Firmware: {deviceInfo.firmwareRevision}
              </Text>
            )}
            {deviceInfo?.hardwareRevision && (
              <Text style={{ color: colors.textColor }}>
                Hardware: {deviceInfo.hardwareRevision}
              </Text>
            )}
            <Text style={{ color: colors.textColor }}>
              Detected erg type: {ergType}
            </Text>
          </View>
        )}

        {connectionLog.length > 0 && (
          <View style={{ margin: 10 }}>
            <Text style={{ fontWeight: 'bold', marginBottom: 4 }}>
              Connection log (step-by-step, matches mobile)
            </Text>
            <View style={{ maxHeight: 200, overflow: 'scroll' }}>
              {connectionLog.map((entry, i) => (
                <Text
                  key={i}
                  style={{
                    color: colors.textColor,
                    fontSize: 12,
                    fontFamily: 'monospace',
                  }}
                >
                  {entry.step}
                  {entry.detail ? `: ${entry.detail}` : ''}
                </Text>
              ))}
            </View>
          </View>
        )}

        {latestData && (
          <>
            <DataTable>
              <DataTable.Header>
                <DataTable.Title>Metric</DataTable.Title>
                <DataTable.Title numeric>Value</DataTable.Title>
              </DataTable.Header>
              {Object.entries(latestData).map(([key, value]) => {
                if (value === undefined) return null;
                return (
                  <DataTable.Row key={key}>
                    <DataTable.Cell>{key}</DataTable.Cell>
                    <DataTable.Cell numeric>{value}</DataTable.Cell>
                  </DataTable.Row>
                );
              })}
            </DataTable>
          </>
        )}

        {dataLog.length > 0 && (
          <Text style={{ margin: 10, color: colors.textColor }}>
            Captured {dataLog.length} notifications. Click "Export to File" to
            download.
          </Text>
        )}
      </Card>
    </View>
  );
};

export default FTMSPage;
